import { saveAs } from 'file-saver';

/**
 * Saving a file the backend returned as base64, which is what most download endpoints answer with.
 * Endpoints answering with a real blob (`AuthService.CallBackendFunctionGetFile`) need none of this —
 * pass their result straight to `saveAs`.
 */
export default class fileHelper {
  /** Decodes base64 into bytes. For a download use saveBase64File instead. */
  static base64ToByteArray(base64Content) {
    const byteCharacters = atob(base64Content);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    return new Uint8Array(byteNumbers);
  }

  /**
   * Reads a base64 payload as UTF-8 text, for the endpoints that answer with a text file the page
   * shows rather than downloads (a stored HTML template, for instance). The backend has already
   * converted the file to UTF-8 on its way out, so this is the plain inverse of that.
   *
   * @param {string} base64Content the file's bytes, base64 encoded
   * @returns {string} the decoded text
   */
  static base64ToUtf8Text(base64Content) {
    const byteArray = fileHelper.base64ToByteArray(base64Content);
    return new TextDecoder('utf-8').decode(byteArray);
  }

  /**
   * A text payload as a File, so it can be posted to an endpoint that takes an uploaded file.
   * A Blob encodes text as UTF-8, which is the format every upload endpoint expects.
   *
   * @param {string} text the file's content
   * @param {string} fileName the name to send it under, extension included
   * @param {string} [contentType] MIME type; omit it when the extension is enough
   * @returns {File} the file to append to a FormData
   */
  static textAsFile(text, fileName, contentType) {
    return new File([text], fileName, contentType ? { type: contentType } : undefined);
  }

  /**
   * Offers a base64 payload to the browser as a downloaded file.
   *
   * @param {string} base64Content the file's bytes, base64 encoded
   * @param {string} fileName the name to save it under, extension included
   * @param {string} [contentType] MIME type; omit it when the extension is enough
   */
  static saveBase64File(base64Content, fileName, contentType) {
    const byteArray = fileHelper.base64ToByteArray(base64Content);
    const blob = contentType ? new Blob([byteArray], { type: contentType }) : new Blob([byteArray]);
    saveAs(blob, fileName);
  }
}
